import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Flex, Button, Input, Table, Thead, Tbody, Tr, Th, Td,
  Text, useToast, FormControl, FormLabel, IconButton,
} from "@chakra-ui/react";
import { FaEdit, FaPlus, FaTrash, FaTimes, FaPaperPlane } from "react-icons/fa";
import PageToolbar from "../../../components/shared/PageToolbar";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import { supplierLabel, sortByLabel } from "../../../tools/supplierLabel";
import { Panel } from "../../../components/shared/Section";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";

const API = process.env.REACT_APP_API_URL;
const RETAILER_FLAGS = ["pnp", "chk", "dis", "mak", "cli", "med", "pet"];
const EMPTY_FORM = { fname: "", useremail: "", password: "", invite: true, pages: [], suppliers: [], retailerAccess: {} };

// Shown names of page grants (2026-10-16, Helena): RepSales = the Rep Sales & Routing screen.
const PAGE_LABEL = { RepSales: "Rep Sales & Routing", StrikeRate: "Strike Rate" };
const pageLabel = (p) => PAGE_LABEL[p] || p;

const Chip = ({ on, label, onClick }) => (
  <Button
    size="xs"
    variant="ghost"
    onClick={onClick}
    aria-pressed={on}
    minW="40px"
    fontWeight="600"
    color={on ? "signal.up" : "ink.400"}
    bg={on ? "signal.upSoft" : "ink.100"}
    _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
  >
    {label}
  </Button>
);

/**
 * Organisation admin's Users screen.
 *
 * Deliberately NOT the master Users screen: only Basic users in the caller's
 * own organisation, no passwords shown anywhere, no user-type choice, no
 * billing toggles. The backend (/org-admin/*) enforces every boundary again —
 * this page is a convenience view of what the org admin is allowed to do.
 */
const OrgUsers = () => {
  const toast = useToast();
  // Suppliers are shown by name where the name really differs from the code (PRIME DENTAL → Curaden, Helena
  // 2026-10-16); the code stays the value that is saved.
  const { user_supplier_access } = useSupplierRetailer();
  const [context, setContext] = useState(null); // { org, suppliers, pages }
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | { ...EMPTY_FORM, userid? }
  const [submitting, setSubmitting] = useState(false);

  const entitledRetailers = RETAILER_FLAGS.filter((f) => context?.org?.[f] === true);

  const load = async () => {
    setLoading(true);
    try {
      const [ctx, usr] = await Promise.all([
        axios.post(`${API}/org-admin/get-context`, {}),
        axios.post(`${API}/org-admin/get-users`, {}),
      ]);
      setContext(ctx.data?.data || null);
      setUsers(usr.data?.data || []);
    } catch (error) {
      toast({ title: "Failed to load users", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => setForm({ ...EMPTY_FORM });
  const openEdit = (u) =>
    setForm({
      userid: u.userid,
      fname: u.fname || "",
      useremail: u.useremail || "",
      password: "",
      pages: u.pages || [],
      suppliers: u.suppliers || [],
      retailerAccess: u.retailer_access || {},
    });

  const toggleIn = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));

  const toggleRetailer = (flag) =>
    setForm((prev) => ({ ...prev, retailerAccess: { ...prev.retailerAccess, [flag]: !prev.retailerAccess[flag] } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (form.userid) {
        await axios.post(`${API}/org-admin/upd-user`, {
          userid: form.userid,
          fname: form.fname,
          useremail: form.useremail,
          pages: form.pages,
          suppliers: form.suppliers,
          retailerAccess: form.retailerAccess,
        });
        toast({ title: "User updated", status: "success", duration: 3000, isClosable: true });
      } else if (form.invite) {
        // No password: the backend emails a registration link and the user
        // sets their own name + password.
        const res = await axios.post(`${API}/org-admin/ins-user`, {
          fname: form.fname,
          useremail: form.useremail,
          pages: form.pages,
          suppliers: form.suppliers,
          retailerAccess: form.retailerAccess,
        });
        toast({
          title: res.data?.data?.emailed ? "Invite sent" : "User created — email failed",
          description: res.data?.message,
          status: res.data?.data?.emailed ? "success" : "warning",
          duration: res.data?.data?.emailed ? 5000 : 20000,
          isClosable: true,
        });
      } else {
        await axios.post(`${API}/org-admin/ins-user`, form);
        toast({ title: "User created", description: `${form.useremail} can now sign in.`, status: "success", duration: 4000, isClosable: true });
      }
      setForm(null);
      load();
    } catch (error) {
      toast({ title: "Save failed", description: error?.response?.data?.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.post(`${API}/org-admin/upd-user`, { userid: u.userid, activeyn: !u.activeyn });
      setUsers((prev) => prev.map((x) => (x.userid === u.userid ? { ...x, activeyn: !u.activeyn } : x)));
    } catch (error) {
      toast({ title: "Failed to update", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleResend = async (u) => {
    try {
      const res = await axios.post(`${API}/org-admin/resend-invite`, { userid: u.userid });
      toast({
        title: res.data?.data?.emailed ? "Invite re-sent" : "Email failed",
        description: res.data?.message,
        status: res.data?.data?.emailed ? "success" : "warning",
        duration: res.data?.data?.emailed ? 4000 : 20000,
        isClosable: true,
      });
    } catch (error) {
      toast({ title: "Failed to resend", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleDelete = async (u) => {
    try {
      const res = await axios.post(`${API}/org-admin/del-user`, { userid: u.userid });
      toast({ title: res.data.title || "User deleted", description: res.data.description, status: "success", duration: 3000, isClosable: true });
      load();
    } catch (error) {
      toast({ title: "Failed to delete", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  return (
    <Box>
      <PageToolbar
        filters={
          <Text fontSize="sm" color="ink.500">
            {context?.org?.org_name || ""} — {users.length} {users.length === 1 ? "user" : "users"}
          </Text>
        }
        actions={
          <Button leftIcon={<FaPlus />} onClick={openAdd}>
            Add user
          </Button>
        }
      />

      {form && (
        <Panel maxW="620px" mb={4}>
          <form onSubmit={handleSubmit}>
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontWeight="600">{form.userid ? `Edit ${form.fname}` : "Add user"}</Text>
              <IconButton icon={<FaTimes />} size="sm" variant="ghost" aria-label="Close" onClick={() => setForm(null)} />
            </Flex>
            <FormControl isRequired mb={2}>
              <FormLabel fontSize="sm">Email (used to sign in)</FormLabel>
              <Input type="email" value={form.useremail} onChange={(e) => setForm({ ...form, useremail: e.target.value })} isReadOnly={!!form.userid} />
            </FormControl>
            {!form.userid && (
              <FormControl mb={2}>
                <Flex align="center" gap={2}>
                  <Chip on={form.invite} label={form.invite ? "Email invite" : "Set password"} onClick={() => setForm({ ...form, invite: !form.invite })} />
                  <Text fontSize="xs" color="ink.500">
                    {form.invite
                      ? "They get an email link and choose their own name and password."
                      : "You set the name and password yourself."}
                  </Text>
                </Flex>
              </FormControl>
            )}
            <FormControl isRequired={!form.invite || !!form.userid} mb={2} display={!form.userid && form.invite ? "none" : "block"}>
              <FormLabel fontSize="sm">Full name</FormLabel>
              <Input value={form.fname} onChange={(e) => setForm({ ...form, fname: e.target.value })} placeholder="ie: Jane Smith" />
            </FormControl>
            {!form.userid && !form.invite && (
              <FormControl isRequired mb={2}>
                <FormLabel fontSize="sm">Password</FormLabel>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </FormControl>
            )}
            <FormControl mb={2}>
              <FormLabel fontSize="sm">Pages</FormLabel>
              <Flex gap={1} wrap="wrap">
                {(context?.pages || []).map((p) => (
                  <Chip key={p} on={form.pages.includes(p)} label={pageLabel(p)} onClick={() => toggleIn("pages", p)} />
                ))}
              </Flex>
            </FormControl>
            <FormControl mb={2}>
              <FormLabel fontSize="sm">Suppliers</FormLabel>
              <Flex gap={1} wrap="wrap">
                {sortByLabel(context?.suppliers, user_supplier_access).map((s) => (
                  <Chip key={s} on={form.suppliers.includes(s)} label={supplierLabel(s, user_supplier_access)} onClick={() => toggleIn("suppliers", s)} />
                ))}
              </Flex>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontSize="sm">Retailers</FormLabel>
              <Flex gap={1} wrap="wrap">
                {entitledRetailers.map((f) => (
                  <Chip key={f} on={!!form.retailerAccess[f]} label={f.toUpperCase()} onClick={() => toggleRetailer(f)} />
                ))}
              </Flex>
            </FormControl>
            <Button type="submit" isLoading={submitting}>
              {form.userid ? "Save changes" : form.invite ? "Send invite" : "Create user"}
            </Button>
          </form>
        </Panel>
      )}

      <Panel padded={false} maxH="calc(100vh - 260px)" overflow="auto">
        {loading ? (
          <TableSkeleton rows={6} minH="240px" />
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={2} py={1} bg="gray.50">Name</Th>
                <Th px={2} py={1} bg="gray.50">Email</Th>
                <Th px={2} py={1} bg="gray.50">Pages</Th>
                <Th px={2} py={1} bg="gray.50">Suppliers</Th>
                <Th px={2} py={1} bg="gray.50">Retailers</Th>
                <Th px={2} py={1} bg="gray.50">Sign in</Th>
                <Th px={2} py={1} bg="gray.50">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.length === 0 && (
                <Tr>
                  <Td colSpan={7} py={6}>
                    <Text fontSize="sm" color="ink.500" textAlign="center">
                      No users yet — add your first user above.
                    </Text>
                  </Td>
                </Tr>
              )}
              {users.map((u) => (
                <Tr key={u.userid}>
                  <Td px={2} py={1} fontWeight="600">{u.fname}</Td>
                  <Td px={2} py={1}>{u.useremail}</Td>
                  <Td px={2} py={1}>{(u.pages || []).map(pageLabel).join(", ") || "-"}</Td>
                  <Td px={2} py={1}>{sortByLabel(u.suppliers, user_supplier_access).map((c) => supplierLabel(c, user_supplier_access)).join(", ") || "-"}</Td>
                  <Td px={2} py={1}>
                    {RETAILER_FLAGS.filter((f) => u.retailer_access?.[f]).map((f) => f.toUpperCase()).join(", ") || "-"}
                  </Td>
                  <Td px={2} py={1}>
                    {u.invite_pending && !u.activeyn ? (
                      <Text as="span" fontSize="xs" fontWeight="600" color="orange.500">
                        Invited
                      </Text>
                    ) : (
                      <Chip on={!!u.activeyn} label={u.activeyn ? "Yes" : "No"} onClick={() => toggleActive(u)} />
                    )}
                  </Td>
                  <Td px={2} py={1}>
                    {u.invite_pending && !u.activeyn && (
                      <IconButton icon={<FaPaperPlane />} size="xs" variant="ghost" aria-label="Resend invite" title="Resend invite" onClick={() => handleResend(u)} />
                    )}
                    <IconButton icon={<FaEdit />} size="xs" variant="ghost" aria-label="Edit" onClick={() => openEdit(u)} />
                    <IconButton icon={<FaTrash />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete" onClick={() => handleDelete(u)} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Panel>
    </Box>
  );
};

export default OrgUsers;
